import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '~/lib/api'

/**
 * A form submit as a mutation whose errors stay in the form: 422 field errors go to their fields,
 * everything else to one sentence above the button. No global toast on top (v1 W23 showed both).
 */
export function useFormMutation<TInput, TResult>(
  fn: (input: TInput) => Promise<TResult>,
  opts: { onSuccess?: (result: TResult, input: TInput) => void; fieldMap?: Record<string, string> } = {},
) {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: fn,
    onMutate: () => {
      setFieldErrors({})
      setFormError(null)
    },
    onSuccess: (result, input) => opts.onSuccess?.(result, input),
    onError: (e) => {
      if (e instanceof ApiError && e.fields && Object.keys(e.fields).length > 0) {
        const mapped = Object.fromEntries(Object.entries(e.fields).map(([k, msg]) => [opts.fieldMap?.[k] ?? k, msg]))
        setFieldErrors(mapped)
        // A field error the form has no field for still needs to be visible.
        if (mapped._) setFormError(mapped._)
      } else setFormError(e instanceof ApiError ? e.message : 'Das hat nicht geklappt. Versuch es noch einmal.')
    },
  })
  return {
    submit: mutation.mutate,
    pending: mutation.isPending,
    fieldErrors,
    formError,
    /** Client-side validation result, shown like server errors. */
    setFieldErrors,
    setFormError,
    reset: () => {
      setFieldErrors({})
      setFormError(null)
    },
  }
}

/** Page title: "Anmelden · SquadCore". */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    const prev = document.title
    document.title = title ? `${title} · SquadCore` : 'SquadCore'
    return () => {
      document.title = prev
    }
  }, [title])
}

/** Only same-origin app paths as redirect targets after login (no open redirect). */
export function safeFrom(from: unknown): string | undefined {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//') || from.startsWith('/\\')) return undefined
  if (/^\/(login|registrieren|passwort-|einladung)/.test(from)) return undefined
  return from
}

export const MIN_PASSWORD = 10
